import { Heart, ShieldCheck } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import CustomShieldCheck from './Icons/shieldCheck';
import { addFavorite, removeFavorite } from '../api/userApi';

// Define the type for the space prop
interface SpaceType {
  id: string;
  ownerName: string | null;
  region: string;
  location: string;
  mainImageUrl: string | null;
  price: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  tags: string[] | null;
  reservationCount: number | null;
  ownerCertified: boolean;
  isFavorite?: boolean;
}

interface SpaceCardProps {
  space: SpaceType;
  isLoggedIn?: boolean;
  onFavoriteChange?: (spaceId: string, isFavorite: boolean) => void;
}

const SpaceCard: React.FC<SpaceCardProps> = ({ space, isLoggedIn = true, onFavoriteChange }) => {
  const navigate = useNavigate();

  const ownerName = space.ownerName || '익명의 사용자';
  const location = space.location;
  const maxReservations = 15;
  const tags = Array.isArray(space.tags) && space.tags.length > 0
    ? space.tags.join(', ')
    : '선택된 옵션 없음';
  const reservationCount = space.reservationCount ?? 0;
  const mainImage = space.mainImageUrl || '/default_place.jpg';

  const [saving, setSaving] = React.useState<boolean>(false);

  const onToggleFavorite = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('user_token');
      if (!token) {
        alert('로그인이 필요합니다.');
        navigate('/login');
        return;
      }

      if (!space.isFavorite) {
        await addFavorite(parseInt(space.id));
      } else {
        await removeFavorite(parseInt(space.id));
      }
      if (onFavoriteChange) onFavoriteChange(space.id, !space.isFavorite);
    } catch (err) {
      // 오류시 처리 (UX에 맞게)
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="
        bg-white shadow rounded-lg p-4 cursor-pointer hover:scale-105 transition
        flex flex-row lg:flex-col relative
      "
      onClick={() => navigate(`/spaces/${space.id}`)}
    >
      {/* 우측 상단 하트 버튼 */}
      {isLoggedIn && (
        <button
          type="button"
          onClick={onToggleFavorite}
          disabled={saving}
          aria-pressed={space.isFavorite}
          aria-label={space.isFavorite ? '관심목록에서 제거' : '관심목록에 추가'}
          title={space.isFavorite ? '관심 해제' : '관심 추가'}
          className={
            `absolute top-2 right-2 z-10
            hover:bg-white focus:outline-none focus:ring-2 focus:ring-blue-400
            ${saving ? 'opacity-60 cursor-wait' : ''}`
          }
        >
          <Heart
            className="w-6 h-6"
            strokeWidth={1.8}
            fill={space.isFavorite ? 'currentColor' : 'none'}
            color="currentColor"
            // @ts-ignore
            className={`w-6 h-6 ${space.isFavorite ? 'text-red-500' : 'text-gray-700'}`}
          />
        </button>
      )}
      
      {/* 이미지 */}
      <img
        src={mainImage}
        alt={ownerName + ' 공간 대표 이미지'}
        className="rounded-lg w-32 h-32 object-cover lg:w-full lg:h-40 mb-4 mr-4 lg:mr-0 flex-shrink-0 select-none"
        draggable={false}
      />
      {/* 공간 정보 표출 */}
      <div className="flex-1 flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <span className="font-bold text-lg text-gray-900 truncate max-w-[80%]">{ownerName}</span>
          {space.ownerCertified && <ShieldCheck className="w-4 h-4 text-blue-500" />}
        </div>
        <span className="text-gray-500 text-sm truncate max-w-full">{location}</span>
        <span className="text-gray-700 text-base font-semibold">{space.price.toLocaleString()}원</span>
        <span className="text-gray-400 text-xs">{tags}</span>
        <span className="text-indigo-500 text-xs">예약 {reservationCount} / {maxReservations}</span>
      </div>
    </div>
  );
};

export default SpaceCard;